(() => {
  const root = document.documentElement;
  const config = window.Padelilli || {};

  const announce = (message) => {
    const region = document.querySelector('[data-cart-live-region]');
    if (region) region.textContent = message;
  };

  const formatMoney = (cents) => {
    const currency = config.currency || 'MXN';
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `$${Math.round(Number(cents || 0) / 100).toLocaleString('es-MX')}`;
    }
  };

  const updateCartCount = async () => {
    if (!config.routes?.cart) return;
    const response = await fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
  };

  const addToCart = async (form, button) => {
    const original = button?.querySelector('span')?.textContent || button?.textContent || '';
    if (button) {
      button.disabled = true;
      const label = button.querySelector('span');
      if (label) label.textContent = 'AGREGANDO…';
    }

    try {
      const response = await fetch(config.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'No pudimos agregar el producto.');
      await updateCartCount();
      announce(`${result.title || 'Producto'} se agregó al carrito.`);
      const label = button?.querySelector('span');
      if (label) label.textContent = 'AGREGADO ✓';
      window.setTimeout(() => { if (label) label.textContent = original; }, 1400);
    } catch (error) {
      announce(error.message || 'No pudimos agregar el producto.');
      const label = button?.querySelector('span');
      if (label) label.textContent = 'INTENTA DE NUEVO';
    } finally {
      window.setTimeout(() => { if (button) button.disabled = false; }, 500);
    }
  };

  const initMobileMenu = (scope = document) => {
    scope.querySelectorAll('[data-site-header]').forEach((header) => {
      if (header.dataset.menuBound) return;
      header.dataset.menuBound = 'true';
      const button = header.querySelector('[data-mobile-menu-button]');
      const nav = header.querySelector('[data-mobile-nav]');
      const openIcon = header.querySelector('[data-menu-open-icon]');
      const closeIcon = header.querySelector('[data-menu-close-icon]');
      if (!button || !nav) return;

      const close = () => {
        nav.classList.remove('open');
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', 'Abrir menú');
        openIcon?.classList.remove('hidden');
        closeIcon?.classList.add('hidden');
      };

      button.addEventListener('click', () => {
        const isOpen = !nav.classList.contains('open');
        nav.classList.toggle('open', isOpen);
        button.setAttribute('aria-expanded', String(isOpen));
        button.setAttribute('aria-label', isOpen ? 'Cerrar menú' : 'Abrir menú');
        openIcon?.classList.toggle('hidden', isOpen);
        closeIcon?.classList.toggle('hidden', !isOpen);
      });
      nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
    });
  };

  const initCarousels = (scope = document) => {
    scope.querySelectorAll('[data-carousel-prev], [data-carousel-next]').forEach((button) => {
      if (button.dataset.carouselBound) return;
      button.dataset.carouselBound = 'true';
      button.addEventListener('click', () => {
        const id = button.dataset.carouselPrev || button.dataset.carouselNext;
        const track = document.getElementById(id);
        if (!track) return;
        const direction = button.dataset.carouselPrev ? -1 : 1;
        track.scrollBy({ left: direction * Math.min(430, track.clientWidth * .78), behavior: 'smooth' });
      });
    });
  };

  const initQuickAdd = (scope = document) => {
    scope.querySelectorAll('form[data-quick-add], form[data-product-form]').forEach((form) => {
      if (form.dataset.cartBound) return;
      form.dataset.cartBound = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        addToCart(form, form.querySelector('button[type="submit"]'));
      });
    });
  };

  const initProduct = (scope = document) => {
    scope.querySelectorAll('[data-product-section]').forEach((section) => {
      if (section.dataset.productBound) return;
      section.dataset.productBound = 'true';
      const mainImage = section.querySelector('.detail-main-image');
      section.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          if (mainImage && thumbnail.dataset.imageSrc) mainImage.src = thumbnail.dataset.imageSrc;
          section.querySelectorAll('[data-product-thumbnail]').forEach((item) => item.classList.remove('active'));
          thumbnail.classList.add('active');
        });
      });

      const quantity = section.querySelector('[data-quantity-input]');
      section.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        if (quantity) quantity.value = Math.max(1, Number(quantity.value || 1) - 1);
      });
      section.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        if (quantity) quantity.value = Number(quantity.value || 1) + 1;
      });

      const select = section.querySelector('[data-variant-select]');
      const json = section.querySelector('[data-product-json]');
      if (!select || !json) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent || '[]'); } catch (_error) { return; }
      select.addEventListener('change', () => {
        const variant = variants.find((item) => String(item.id) === select.value);
        if (!variant) return;
        const idInput = section.querySelector('[data-product-variant-input]');
        if (idInput) idInput.value = variant.id;
        const price = section.querySelector('[data-current-price]');
        const compare = section.querySelector('[data-compare-price]');
        const discount = section.querySelector('[data-price-discount], [data-product-discount]');
        const button = section.querySelector('[data-add-to-cart]');
        const label = section.querySelector('[data-add-label]');
        if (price) price.textContent = formatMoney(variant.price);
        if (compare) {
          compare.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
          compare.hidden = !(variant.compare_at_price > variant.price);
        }
        if (discount) {
          const amount = variant.compare_at_price > variant.price ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
          discount.textContent = amount ? `-${amount}%` : '';
          discount.hidden = !amount;
        }
        if (button) button.disabled = !variant.available;
        if (label) label.textContent = variant.available ? 'AGREGAR AL CARRITO' : 'AGOTADA';
        if (variant.featured_image?.src && mainImage) mainImage.src = `${variant.featured_image.src}${variant.featured_image.src.includes('?') ? '&' : '?'}width=1100`;
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', nextUrl);
      });
    });
  };

  const closeFacets = () => {
    document.body.classList.remove('facets-open');
    document.querySelectorAll('[data-facets-drawer]').forEach((drawer) => drawer.classList.remove('open'));
  };

  const replaceCollection = async (url, updateHistory = true) => {
    const current = document.querySelector('[data-collection-section]');
    if (!current) { window.location.href = url; return; }
    current.classList.add('is-loading');
    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('No se pudo actualizar el catálogo.');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const replacement = doc.querySelector('[data-collection-section]');
      if (!replacement) throw new Error('Respuesta incompleta.');
      current.replaceWith(replacement);
      if (updateHistory) window.history.pushState({}, '', url);
      closeFacets();
      initialize(replacement);
      replacement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (_error) {
      window.location.href = url;
    }
  };

  const cleanFormUrl = (form) => {
    const url = new URL(form.action, window.location.origin);
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    });
    return url.toString();
  };

  const initFacets = (scope = document) => {
    scope.querySelectorAll('[data-collection-section]').forEach((section) => {
      if (section.dataset.facetsBound) return;
      section.dataset.facetsBound = 'true';
      const drawer = section.querySelector('[data-facets-drawer]');
      section.querySelector('[data-facets-open]')?.addEventListener('click', () => {
        drawer?.classList.add('open');
        document.body.classList.add('facets-open');
      });
      section.querySelector('[data-facets-close]')?.addEventListener('click', closeFacets);
      section.querySelector('[data-facets-overlay]')?.addEventListener('click', closeFacets);

      const form = section.querySelector('[data-facet-form]');
      form?.addEventListener('submit', (event) => {
        event.preventDefault();
        replaceCollection(cleanFormUrl(form));
      });

      const sort = section.querySelector('[data-sort-select]');
      sort?.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        replaceCollection(url.toString());
      });

      section.querySelectorAll('.pagination a').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          replaceCollection(link.href);
        });
      });
    });
  };

  const initRecommendations = (scope = document) => {
    scope.querySelectorAll('product-recommendations[data-url]').forEach((element) => {
      if (element.dataset.recommendationsBound) return;
      element.dataset.recommendationsBound = 'true';
      if (element.innerHTML.trim()) return;
      fetch(element.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const recommendations = html.querySelector('product-recommendations');
          if (recommendations?.innerHTML.trim()) {
            element.innerHTML = recommendations.innerHTML;
            initQuickAdd(element);
          }
        })
        .catch(() => {});
    });
  };

  const initialize = (scope = document) => {
    initMobileMenu(scope);
    initCarousels(scope);
    initQuickAdd(scope);
    initProduct(scope);
    initFacets(scope);
    initRecommendations(scope);
  };

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-collection-section]')) replaceCollection(window.location.href, false);
  });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeFacets(); });
  root.classList.add('js');
})();
